// Remote Punch API calls (user-service routes/remotePunchRoutes.js).
import { apiFetch } from "../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || "";

async function readJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    const err = new Error(body?.message || body?.error || `Request failed (${res.status})`);
    err.code = body?.code;
    err.status = res.status;
    throw err;
  }
  return body?.data ?? body;
}

const qs = (params = {}) => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
  const s = new URLSearchParams(clean).toString();
  return s ? `?${s}` : "";
};

export const remotePunchApi = {
  /** → { enabled } for the caller's company (superadmin: pass clientName). */
  status: (params) => apiFetch(`${BASE_URL}/api/remote-punch/status${qs(params)}`).then(readJson),
  me: () => apiFetch(`${BASE_URL}/api/remote-punch/me`).then(readJson),
  acceptConsent: () => apiFetch(`${BASE_URL}/api/remote-punch/me/consent`, { method: "POST" }).then(readJson),
  /** → { ar, en } area name of a position (remote employees only), or null. */
  myArea: (lat, lng) => apiFetch(`${BASE_URL}/api/remote-punch/me/area${qs({ lat, lng })}`).then(readJson),

  overview: (params) => apiFetch(`${BASE_URL}/api/remote-punch/overview${qs(params)}`).then(readJson),
  /** → { employees, total } (remote employees first). */
  listEmployees: (params) => apiFetch(`${BASE_URL}/api/remote-punch/employees${qs(params)}`).then(readJson),
  /** → { companies, departments } to filter the employee list by. */
  meta: (params) => apiFetch(`${BASE_URL}/api/remote-punch/meta${qs(params)}`).then(readJson),
  setEmployee: (infinityCode, body, clientName) =>
    apiFetch(`${BASE_URL}/api/remote-punch/employees/${encodeURIComponent(infinityCode)}${qs({ clientName })}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }).then(readJson),
  listGroups: (params) => apiFetch(`${BASE_URL}/api/remote-punch/groups${qs(params)}`).then(readJson),
  setGroup: (groupId, body, clientName) =>
    apiFetch(`${BASE_URL}/api/remote-punch/groups/${encodeURIComponent(groupId)}${qs({ clientName })}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }).then(readJson),

  /** items: [{ infinityCode, at }] → the remote punches among them. */
  lookupPunches: (items, clientName) =>
    apiFetch(`${BASE_URL}/api/remote-punch/punches/lookup${qs({ clientName })}`, {
      method: "POST",
      body: JSON.stringify({ items }),
    }).then(readJson),

  /** Settings are per company. */
  getSettings: (clientName) => apiFetch(`${BASE_URL}/api/remote-punch/settings${qs({ clientName })}`).then(readJson),
  /** Accounts that can receive the company's alerts, by name, code or email. */
  searchRecipients: (clientName, search) =>
    apiFetch(`${BASE_URL}/api/remote-punch/settings/recipients${qs({ clientName, search })}`).then(readJson),
  updateSettings: (clientName, body) =>
    apiFetch(`${BASE_URL}/api/remote-punch/settings${qs({ clientName })}`, { method: "PUT", body: JSON.stringify(body) }).then(readJson),
  changeMonitoring: (clientName, action) =>
    apiFetch(`${BASE_URL}/api/remote-punch/settings/monitoring${qs({ clientName })}`, { method: "POST", body: JSON.stringify({ action }) }).then(readJson),
  getAlert: (id) => apiFetch(`${BASE_URL}/api/remote-punch/alerts/${encodeURIComponent(id)}`).then(readJson),
};

const punchKey = (code, at) => `${String(code)}|${new Date(at).getTime()}`;

/**
 * Attendance rows (each with employeeId + checkInAt / checkOutAt instants) →
 * the same rows with `remoteIn` / `remoteOut` set on remote punches. Rows
 * with no remote punch come back unchanged. Never throws: the table must
 * render without pins rather than not at all.
 */
export const attachRemotePunches = async (rows) => {
  const items = [];
  for (const r of rows || []) {
    if (r?.checkInAt) items.push({ infinityCode: r.employeeId, at: r.checkInAt });
    if (r?.checkOutAt) items.push({ infinityCode: r.employeeId, at: r.checkOutAt });
  }
  if (!items.length) return rows;
  try {
    const found = [];
    for (let i = 0; i < items.length; i += 1000) {
      found.push(...(await remotePunchApi.lookupPunches(items.slice(i, i + 1000))));
    }
    if (!found.length) return rows;
    const byKey = new Map(found.map((p) => [punchKey(p.infinityCode, p.at), p]));
    return rows.map((r) => {
      const remoteIn = r.checkInAt ? byKey.get(punchKey(r.employeeId, r.checkInAt)) || null : null;
      const remoteOut = r.checkOutAt ? byKey.get(punchKey(r.employeeId, r.checkOutAt)) || null : null;
      return remoteIn || remoteOut ? { ...r, remoteIn, remoteOut } : r;
    });
  } catch (err) {
    console.warn("Remote punch lookup failed:", err?.message || err);
    return rows;
  }
};

/** Google Maps link for a position. */
export const googleMapsUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

/** The area name in the viewer's language, falling back to the other one. */
export const areaNameFor = (punch, language) =>
  (language === "ar" ? punch?.areaNameAr || punch?.areaNameEn : punch?.areaNameEn || punch?.areaNameAr) || null;

/**
 * What the punch request tells the server about the device. Only facts the
 * server can't see itself: touch points (an iPad says "Macintosh") and the
 * device clock.
 */
export const devicePayload = () => {
  let timeZone = null;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch { /* old browser */ }
  return {
    device: {
      maxTouchPoints: typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0,
      timeZone,
      tzOffsetMinutes: new Date().getTimezoneOffset(),
    },
  };
};

const MOBILE_UA = /iphone|ipod|android.+mobile|windows phone|iemobile|opera mini|blackberry|bb10|mobile.+firefox|webos/i;
const TABLET_UA = /ipad|android|tablet|silk|kindle|playbook/i;

/** Same classification as the server (utils/remotePunchRules.js), for the on-screen hint only. */
export const isHandheldDevice = () => {
  if (typeof navigator === "undefined") return true;
  if (navigator.userAgentData?.mobile) return true;
  const ua = navigator.userAgent || "";
  if (MOBILE_UA.test(ua) || TABLET_UA.test(ua)) return true;
  return /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
};
